// One-click InDesign exports for the coming Sunday, from the saved seasonal plan
// and the Sunday's saved preacher and readings. Used on the home page shortcut.
import React, { useEffect, useState } from 'react';
import {
  BulletinKey, BundleService, ALL_BULLETINS, createIndesignExports, downloadExport,
  fetchBundleServices, findComingSunday,
} from './indesignExportBundle';

const BUTTONS: { label: string; only: BulletinKey[] }[] = [
  { label: 'All three', only: ALL_BULLETINS },
  { label: '8 am', only: ['8am'] },
  { label: '10 am', only: ['10am'] },
  { label: '12:30', only: ['sp'] },
];

const formatSunday = (date: string) => new Date(date + 'T12:00:00')
  .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

export const QuickExport: React.FC = () => {
  const [services, setServices] = useState<BundleService[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchBundleServices(6)
      .then(list => { if (!cancelled) setServices(list); })
      .catch(() => { if (!cancelled) { setServices([]); setError('Could not load the coming Sunday.'); } });
    return () => { cancelled = true; };
  }, []);

  const sunday = services ? findComingSunday(services) : null;

  const run = async (label: string, only: BulletinKey[]) => {
    if (!services || !sunday) return;
    setBusy(label); setError(''); setNotice('');
    try {
      const files = await createIndesignExports(sunday, services, only);
      files.forEach(file => downloadExport(file.blob, file.filename));
      setNotice(files.length > 1 ? `Downloaded ${files.length} files.` : `Downloaded ${files[0].filename}.`);
    } catch (err: any) {
      const detail = err.response?.data?.error;
      setError(typeof detail === 'string' ? detail : detail?.message || err.message || 'The export did not work. Please try again.');
    } finally { setBusy(null); }
  };

  if (services === null) return <p className="quick-export-status">Loading the coming Sunday…</p>;
  if (!sunday) return <p className="quick-export-status">{error || 'No coming Sunday with a 10 am service.'}</p>;

  return (
    <div className="quick-export">
      <p className="quick-export-date">Export for {formatSunday(sunday)}</p>
      <div className="quick-export-buttons">
        {BUTTONS.map(button => (
          <button key={button.label} type="button" disabled={busy !== null}
            className={button.only.length > 1 ? 'quick-export-all' : undefined}
            onClick={() => run(button.label, button.only)}>
            {busy === button.label ? 'Exporting…' : button.label}
          </button>
        ))}
      </div>
      {notice && <p className="quick-export-status" role="status">{notice}</p>}
      {error && <p className="quick-export-error" role="alert">{error}</p>}
    </div>
  );
};

export default QuickExport;
